import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";
import { resolveAssetUrl } from "@/lib/api";

const LINKS = [
  { id: "story", label: "The Story" },
  { id: "menu", label: "The Menu" },
  { id: "grill", label: "The Grill" },
  { id: "visit", label: "Visit Us" },
];

export default function Nav() {
  const { content } = useSiteContent();
  const brand = content?.brand || {};

  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const jump = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[rgba(26,14,8,0.72)] border-b border-warm" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <button onClick={() => jump("hero")} data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src={resolveAssetUrl(brand.logo_url)}
            alt={brand.name}
            className="w-11 h-11 rounded-full object-cover ring-1 ring-[color:var(--gold)]/40 transition group-hover:ring-[color:var(--gold)]"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif-display text-[color:var(--gold-hi)] text-xl">{brand.name}</span>
            <span className="text-[10px] tracking-[0.35em] uppercase text-muted-warm">Restaurant</span>
          </div>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => jump(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="relative text-sm tracking-wide text-cream/85 hover:text-[color:var(--gold-hi)] transition-colors after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[color:var(--gold)] hover:after:w-full after:transition-all after:duration-500"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <button
          onClick={() => jump("menu")}
          data-testid="nav-cta-menu"
          className="hidden md:inline-flex items-center gap-2 rounded-full border border-[color:var(--gold)]/60 text-[color:var(--gold-hi)] px-5 py-2 text-sm tracking-wide hover:bg-[color:var(--gold)] hover:text-[color:var(--bg)] transition-colors"
        >
          View Menu
        </button>

        <button className="md:hidden text-cream p-2" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-panel" className="md:hidden backdrop-blur-xl bg-[rgba(26,14,8,0.94)] border-t border-warm">
          <div className="px-6 py-6 flex flex-col gap-4">
            {LINKS.map((l) => (
              <button key={l.id} onClick={() => jump(l.id)} data-testid={`nav-mobile-link-${l.id}`} className="text-left text-cream text-lg font-serif-display">
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
